import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import HuttonsWhiteLogo from '../../img/Huttons_white.png';
import { savePdfDocument } from './savePdfDocument';
import { getClosingFees, getDownPaymentPlan, type AffordabilityPropertyType } from './affordabilityDownPayment';

/**
 * Max Affordability Calculator — client report.
 *
 * Layout (A4 portrait):
 *   1. Cover: same dark format as the other calculator reports (agent card, client).
 *   2. Summary: headline affordability for each exported property type, the
 *      borrower profile and key assumptions.
 *   3. One page per property type (Private / EC / HDB): how the max loan is built,
 *      the property being considered, how to bridge any shortfall, upfront costs.
 *   4. Notes: the rules and formulas behind every figure.
 *
 * jsPDF's built-in Helvetica is WinAnsi-only: keep every string ASCII (no em dash,
 * arrows, multiply sign or bullets) or the glyphs render spaced out / truncated.
 */

// ── Palette ──────────────────────────────────────────────────────────────
type RGB = [number, number, number];
const NAVY: RGB = [0, 31, 67];
const ORANGE: RGB = [245, 130, 32];
const INK: RGB = [31, 41, 55];
const MUTED: RGB = [107, 114, 128];
const LINE: RGB = [226, 229, 236];
const SOFT: RGB = [245, 247, 250];
const WHITE: RGB = [255, 255, 255];
const GREEN: RGB = [21, 128, 61];
const GREEN_SOFT: RGB = [232, 246, 237];
const RED: RGB = [185, 28, 28];
const RED_SOFT: RGB = [253, 237, 237];
const AMBER: RGB = [161, 82, 8];
const AMBER_SOFT: RGB = [254, 244, 228];
// Cover page — same palette as the other calculator reports (buyingCalculatorPdfExport.ts).
const COVER_ORANGE: RGB = [255, 152, 0];
const COVER_BG: RGB = [15, 23, 42];
const COVER_FOOTER_BG: RGB = [20, 30, 48];
const COVER_CARD_BG: RGB = [30, 41, 59];

const TYPE_META: Record<AffordabilityPropertyType, {
  label: string;
  title: string;
  rule: string;
  accent: RGB;
  soft: RGB;
}> = {
  private: { label: 'Private', title: 'Private Property', rule: 'TDSR 55%', accent: [234, 108, 0], soft: [253, 240, 229] },
  ec: { label: 'EC', title: 'Executive Condominium', rule: 'MSR 30% + TDSR 55%', accent: [123, 31, 162], soft: [243, 233, 248] },
  hdb: { label: 'HDB', title: 'HDB Resale Flat', rule: 'MSR 30% + TDSR 55%', accent: [0, 137, 123], soft: [229, 244, 242] },
};

const PAGE_W = 210;
const PAGE_H = 297;
const M = 14;
const CW = PAGE_W - M * 2;
/** Content must stay above this line — the footer sits below it. */
const CONTENT_BOTTOM = PAGE_H - 16;

const SELF_EMPLOYED_HAIRCUT = 0.70;
const OTHER_ANNUAL_HAIRCUT = 0.70;

// ── Types ─────────────────────────────────────────────────────────────────

export interface AgentInfo {
  Name?: string;
  CEANumber?: string;
  Mobile?: string;
  Email?: string;
  Photo?: string;
}

interface BorrowerState {
  age: number;
  monthlyIncome: number;
  otherAnnualIncome: number;
  isSelfEmployed: boolean;
  existingMonthlyDebt: number;
}

export interface AffordabilityPdfInputs {
  primary: BorrowerState;
  includeAdditionalBorrower: boolean;
  additional: BorrowerState;
  /** Tenure the buyer asked for (each type then applies its own cap). */
  tenure: number;
  ltv: 75 | 55;
  bankRate: number;
  bankRateLabel: string;
  stressTestRate: number;
  stressTestRateLabel: string;
}

/** Figures shared by every property type. */
export interface AffordabilityPdfSummary {
  totalRecognisedIncome: number;
  totalRawIncome: number;
  totalExistingDebt: number;
  grossTDSR: number;
  availableTDSR: number;
  iwaa: number;
  effectiveAgeUsed: number;
  ageBasedCap: number;
  /** Price being tested in the per-type "property you're considering" section. */
  propertyPrice: number;
  requiredLoan: number;
  cashAvailable: number;
  cpfAvailable: number;
  totalFundsAvailable: number;
}

/** One property type's figures — mirrors that column of the on-screen Affordability Overview. */
export interface AffordabilityPdfTypeReport {
  type: AffordabilityPropertyType;
  maxLoan: number;
  maxPropertyPrice: number;
  /** Max property price is a Reverse Mode target rather than a computed limit. */
  maxPropertyPriceIsTarget?: boolean;
  loanTenure: number;
  /** Max tenure this property type allows at the applied LTV. */
  maxTenureForType: number;
  /** Loan the property considered needs at this type's LTV. */
  requiredLoan: number;
  /** How this type's loan is sized — HDB may be on an HDB loan with its own terms. */
  basis: {
    ltv: number;
    stressRate: number;
    stressRateLabel: string;
    interestRate: number;
    cashPercent: number;
    maxAge: number;
    hdbLoan: boolean;
  };
  /** Whether the buyer may buy this type (EC / HDB schemes, HDB loan). */
  eligibility: { eligible: boolean; reasons: string[]; notes: string[] };
  instalmentCap: number;
  affordableToday: number;
  /** EC / HDB only — MSR on full income; the ceiling is an eligibility flag, not a cap. */
  msr?: {
    grossMSR: number;
    incomeCeiling: number;
    aboveCeiling: boolean;
    msrBinding: boolean;
    ceilingLabel: string;
    /** What being above the ceiling means, e.g. "bank loan only, no HDB loan". */
    ceilingConsequence: string;
  };
  loanShortfall: number;
  monthlyMortgageRequired: number;
  stressInstalment: number;
  tdsrExtraIncome: number;
  msrExtraIncome: number;
  extraMonthlyIncomeNeeded: number;
  supportRule: 'TDSR' | 'MSR';
  pledgedFundsNeeded: number;
  unpledgedFundsNeeded: number;
  minimumCashNeeded: number;
  /** Pledged / unpledged mix planned on screen — only for the type shown there. */
  plannedMix?: { unpledged: number; pledged: number };
}

export interface AffordabilityStampDutyPdf {
  bsd: number;
  absd: number;
  total: number;
  propertyPrice: number;
  /** Effective ABSD % after any remission */
  absdRatePercent: number;
  absdRemissionApplied: boolean;
  absdRateBeforeRemissionPercent?: number;
  absdAmountBeforeRemission?: number;
}

export interface AffordabilityPdfData {
  inputs: AffordabilityPdfInputs;
  summary: AffordabilityPdfSummary;
  /** Property types to report, in page order. */
  reports: AffordabilityPdfTypeReport[];
  profileName?: string;
  primaryBorrowerName?: string;
  additionalBorrowerName?: string;
  agentInfo?: AgentInfo;
  stampDuty: AffordabilityStampDutyPdf;
}

interface Logo { dataUrl: string; aspect: number }

// ── Maths / formatting ───────────────────────────────────────────────────

const money = (v: number): string => `$${Math.round(v).toLocaleString('en-SG')}`;
const pct = (v: number, decimals = 2): string => `${v.toFixed(decimals)}%`;

const calcInstalment = (principal: number, annualRate: number, years: number): number => {
  if (principal <= 0 || years <= 0) return 0;
  const r = annualRate / 100 / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  const f = Math.pow(1 + r, n);
  return principal * (r * f) / (f - 1);
};

const recognisedIncome = (b: BorrowerState): number =>
  (b.isSelfEmployed ? b.monthlyIncome * SELF_EMPLOYED_HAIRCUT : b.monthlyIncome)
  + (b.otherAnnualIncome / 12) * OTHER_ANNUAL_HAIRCUT;

/** Monthly instalment on the max loan, at the bank (repayment) rate. */
/** The HDB report when it is sized on an HDB loan. */
const hdbLoanReport = (data: AffordabilityPdfData): AffordabilityPdfTypeReport | undefined =>
  data.reports.find((r) => r.type === 'hdb' && r.basis.hdbLoan);

const maxLoanInstalment = (r: AffordabilityPdfTypeReport): number =>
  Math.round(calcInstalment(r.maxLoan, r.basis.interestRate, r.loanTenure));
/** "2.6% HDB loan rate" / "3.5% bank rate". */
const instalmentRateLabel = (r: AffordabilityPdfTypeReport): string =>
  `${pct(r.basis.interestRate)} ${r.basis.hdbLoan ? 'HDB loan rate' : 'bank rate'}`;

// ── Drawing primitives ───────────────────────────────────────────────────

interface TextOpts {
  size?: number;
  bold?: boolean;
  italic?: boolean;
  color?: RGB;
  align?: 'left' | 'center' | 'right';
  maxWidth?: number;
  lineHeight?: number;
}

/** Line height in mm for a font size in pt. */
const lineMm = (size: number, factor = 1.3): number => size * 0.3528 * factor;

const setFont = (doc: jsPDF, o: TextOpts) => {
  const style = o.bold && o.italic ? 'bolditalic' : o.bold ? 'bold' : o.italic ? 'italic' : 'normal';
  doc.setFont('helvetica', style);
  doc.setFontSize(o.size ?? 9);
};

const wrap = (doc: jsPDF, str: string, width: number, o: TextOpts = {}): string[] => {
  setFont(doc, o);
  return doc.splitTextToSize(str, width) as string[];
};

/** Writes (optionally wrapped) text; returns the height used in mm. */
const write = (doc: jsPDF, str: string, x: number, y: number, o: TextOpts = {}): number => {
  setFont(doc, o);
  doc.setTextColor(...(o.color ?? INK));
  const factor = o.lineHeight ?? 1.3;
  const lines = o.maxWidth ? (doc.splitTextToSize(str, o.maxWidth) as string[]) : [str];
  doc.text(lines, x, y, { align: o.align ?? 'left', lineHeightFactor: factor });
  return lines.length * lineMm(o.size ?? 9, factor);
};

const fillRound = (doc: jsPDF, x: number, y: number, w: number, h: number, fill: RGB, r = 2) => {
  doc.setFillColor(...fill);
  doc.roundedRect(x, y, w, h, r, r, 'F');
};

const drawLogo = (doc: jsPDF, logo: Logo | undefined, x: number, y: number, h: number) => {
  if (logo) {
    doc.addImage(logo.dataUrl, 'PNG', x, y, h * logo.aspect, h);
  } else {
    write(doc, 'HUTTONS', x, y + h * 0.8, { size: h * 2.2, bold: true, color: WHITE });
  }
};

/** Pill with centred text; returns its width. */
const pill = (doc: jsPDF, text: string, x: number, y: number, fill: RGB, color: RGB, size = 7, align: 'left' | 'right' = 'left'): number => {
  setFont(doc, { size, bold: true });
  const w = doc.getTextWidth(text) + 5;
  const h = lineMm(size, 1) + 2.6;
  const left = align === 'right' ? x - w : x;
  fillRound(doc, left, y, w, h, fill, h / 2);
  write(doc, text, left + w / 2, y + h / 2 + lineMm(size, 1) * 0.35, { size, bold: true, color, align: 'center' });
  return w;
};

/** Section heading with a numbered disc (or a bar when unnumbered). Returns the next y. */
const sectionTitle = (doc: jsPDF, y: number, title: string, accent: RGB, step?: number): number => {
  if (step != null) {
    doc.setFillColor(...accent);
    doc.circle(M + 3, y - 1.3, 3, 'F');
    write(doc, String(step), M + 3, y - 0.1, { size: 8, bold: true, color: WHITE, align: 'center' });
    write(doc, title, M + 8.5, y, { size: 12, bold: true, color: NAVY });
  } else {
    doc.setFillColor(...accent);
    doc.rect(M, y - 4, 1.2, 5, 'F');
    write(doc, title, M + 3.5, y, { size: 12, bold: true, color: NAVY });
  }
  return y + 5;
};

/** Grey stat tile: label, big value, optional sub-line. */
const statTile = (
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
    label: string,
    value: string,
    sub?: string,
  o: { fill?: RGB; labelColor?: RGB; valueColor?: RGB; subColor?: RGB; valueSize?: number } = {},
) => {
  fillRound(doc, x, y, w, h, o.fill ?? SOFT);
  write(doc, label.toUpperCase(), x + 3.5, y + 5.3, { size: 6.3, bold: true, color: o.labelColor ?? MUTED, maxWidth: w - 7 });
  write(doc, value, x + 3.5, y + 12.8, { size: o.valueSize ?? 13, bold: true, color: o.valueColor ?? NAVY });
  if (sub) write(doc, sub, x + 3.5, y + 17.4, { size: 6.4, color: o.subColor ?? MUTED, maxWidth: w - 7, lineHeight: 1.2 });
};

/** Full-width coloured note with a bold lead-in. Returns the next y. */
const noteBox = (doc: jsPDF, y: number, lead: string, body: string, fill: RGB, color: RGB): number => {
  const size = 8;
  const textW = CW - 10;
  setFont(doc, { size, bold: true });
  const leadW = doc.getTextWidth(lead) + 1.5;
  // First line continues after the bold lead-in; the rest wraps at full width.
  setFont(doc, { size });
  const words = body.split(' ');
  let first = '';
  while (words.length > 0) {
    const next = first ? `${first} ${words[0]}` : words[0];
    if (doc.getTextWidth(next) > textW - leadW) break;
    first = next;
    words.shift();
  }
  const rest = words.length > 0 ? wrap(doc, words.join(' '), textW, { size }) : [];
  const h = 5 + (1 + rest.length) * lineMm(size);
  fillRound(doc, M, y, CW, h, fill);
  doc.setFillColor(...color);
  doc.rect(M, y, 1.2, h, 'F');
  const ty = y + 4.6;
  write(doc, lead, M + 5, ty, { size, bold: true, color });
  write(doc, first, M + 5 + leadW, ty, { size, color: INK });
  rest.forEach((line, i) => write(doc, line, M + 5, ty + (i + 1) * lineMm(size), { size, color: INK }));
  return y + h + 1;
};

/** Small right-pointing chevron between flow tiles. */
const chevron = (doc: jsPDF, cx: number, cy: number, color: RGB) => {
  doc.setDrawColor(...color);
  doc.setLineWidth(0.6);
  doc.line(cx - 1, cy - 2, cx + 1, cy);
  doc.line(cx + 1, cy, cx - 1, cy + 2);
};

const cleanTableStyles = {
  theme: 'plain' as const,
  styles: {
    font: 'helvetica',
    fontSize: 8,
    textColor: INK,
    cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
    lineColor: LINE,
    lineWidth: { bottom: 0.2 },
    valign: 'middle' as const,
  },
  headStyles: { fillColor: SOFT, textColor: NAVY, fontStyle: 'bold' as const, fontSize: 7.5 },
};

// ── Page chrome ──────────────────────────────────────────────────────────

const pageHeader = (doc: jsPDF, title: string, accent: RGB, logo?: Logo) => {
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE_W, 15, 'F');
  drawLogo(doc, logo, M, 4.2, 6.5);
  write(doc, 'MAX AFFORDABILITY CALCULATOR', PAGE_W - M, 6.6, { size: 6.5, bold: true, color: ORANGE, align: 'right' });
  write(doc, title, PAGE_W - M, 11, { size: 9, bold: true, color: WHITE, align: 'right' });
  doc.setFillColor(...accent);
  doc.rect(0, 15, PAGE_W, 1.2, 'F');
};

const pageFooter = (doc: jsPDF, page: number, total: number, agent?: AgentInfo) => {
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.3);
  doc.line(M, PAGE_H - 12, PAGE_W - M, PAGE_H - 12);
  const who = agent?.Name
    ? [`Prepared by ${agent.Name}`, agent.CEANumber ? `CEA ${agent.CEANumber}` : '', agent.Mobile ?? ''].filter(Boolean).join('  |  ')
    : 'Max Affordability Calculator';
  write(doc, who, M, PAGE_H - 7.5, { size: 7, color: MUTED });
  write(doc, `Page ${page} of ${total}`, PAGE_W - M, PAGE_H - 7.5, { size: 7, color: MUTED, align: 'right' });
};

// ── Images ───────────────────────────────────────────────────────────────

const loadImageEl = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const t = setTimeout(() => reject(new Error('timeout')), 10000);
    img.onload = () => { clearTimeout(t); resolve(img); };
    img.onerror = () => { clearTimeout(t); reject(new Error('failed')); };
    img.src = url;
  });

/** Agent photo clipped to a circle; false when there's none or it fails to load. */
const drawAgentPhoto = async (doc: jsPDF, agent: AgentInfo, cx: number, cy: number, radius: number): Promise<boolean> => {
  if (!agent.Photo) return false;
  try {
    const photoPath = agent.Photo.startsWith('/') ? agent.Photo.substring(1) : agent.Photo;
    const img = await Promise.race([
      loadImageEl(`https://portal.huttonsgroup.com/${photoPath}`),
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
    ]);
    const px = Math.max(256, Math.round(radius * 24));
    const canvas = document.createElement('canvas');
    canvas.width = px;
    canvas.height = px;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // Cover-fit the photo into the circle.
    const scale = Math.max(px / img.width, px / img.height);
    const sw = img.width * scale;
    const sh = img.height * scale;
    ctx.beginPath();
    ctx.arc(px / 2, px / 2, px / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, (px - sw) / 2, (px - sh) / 2, sw, sh);
    doc.addImage(canvas.toDataURL('image/png'), 'PNG', cx - radius, cy - radius, radius * 2, radius * 2);
    return true;
  } catch {
    return false;
  }
};

// ── Page 1: Summary ──────────────────────────────────────────────────────

const typeDescription = (r: AffordabilityPdfTypeReport): string => {
  if (r.type === 'private') {
    return 'Condominiums and landed homes. All your monthly debt repayments, including this home loan, must stay within 55% of your income (TDSR).';
  }
  const ceiling = r.msr ? `${money(r.msr.incomeCeiling)} ${r.msr.ceilingLabel}` : 'income ceiling';
  if (r.type === 'ec') {
    return `Executive condominiums bought from the developer. The home loan instalment must stay within 30% of income (MSR) and all debts within 55% (TDSR). Household income must be within the ${ceiling} to buy one.`;
  }
  return r.basis.hdbLoan
    ? `HDB resale flats on an HDB loan (${pct(r.basis.interestRate)}): the same MSR (30%) and TDSR (55%) tests as an EC, tested at HDB's ${pct(r.basis.stressRate)} floor, ${r.basis.ltv}% LTV and a maximum tenure of ${r.maxTenureForType} years. Household income must be within the ${ceiling}.`
    : `HDB resale flats on a bank loan. The same MSR (30%) and TDSR (55%) tests as an EC and a maximum tenure of ${r.maxTenureForType} years. Above the ${ceiling} only a bank loan is available.`;
};

/** One wide headline card — single-type export. */
const drawHeadlineSingle = (doc: jsPDF, data: AffordabilityPdfData, r: AffordabilityPdfTypeReport, y: number): number => {
  const meta = TYPE_META[r.type];
  const h = 62;
  fillRound(doc, M, y, CW, h, SOFT, 3);
  doc.setFillColor(...meta.accent);
  doc.rect(M, y, 2, h, 'F');

  write(doc, `${meta.title.toUpperCase()}  |  ${meta.rule}`, M + 8, y + 9, { size: 7.5, bold: true, color: meta.accent });
  write(doc, r.maxPropertyPriceIsTarget ? 'Target property price' : 'You can afford a property of up to', M + 8, y + 17, { size: 9.5, color: MUTED });
  write(doc, money(r.maxPropertyPrice), M + 8, y + 30, { size: 28, bold: true, color: NAVY });
  if (!r.eligibility.eligible) pill(doc, 'NOT ELIGIBLE', M + CW - 8, y + 6, RED, WHITE, 7.5, 'right');

  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.3);
  doc.line(M + 8, y + 36, M + CW - 8, y + 36);

  const funds = data.summary.totalFundsAvailable > 0;
  const stats: [string, string, string][] = [
    ['Max loan', money(r.maxLoan), `${r.basis.ltv}% LTV${r.basis.hdbLoan ? ', HDB loan' : ''}`],
    ['Loan tenure', `${r.loanTenure} years`, `max ${r.maxTenureForType} yrs for ${meta.label}`],
    ['Est. monthly instalment', money(maxLoanInstalment(r)), `at ${instalmentRateLabel(r)}`],
    funds
      ? ['Affordable today', money(r.affordableToday), 'with your cash + CPF']
      : ['Max monthly instalment', money(r.instalmentCap), r.type === 'private' ? 'TDSR, after debts' : 'lower of MSR / TDSR'],
  ];
  const colW = (CW - 16) / 4;
  stats.forEach(([label, value, sub], i) => {
    const x = M + 8 + i * colW;
    write(doc, label.toUpperCase(), x, y + 43, { size: 6.3, bold: true, color: MUTED });
    write(doc, value, x, y + 50.5, { size: 12, bold: true, color: i === 3 && funds ? meta.accent : NAVY });
    write(doc, sub, x, y + 55.5, { size: 6.5, color: MUTED, maxWidth: colW - 3 });
  });
  return y + h + 8;
};

/** Three cards side by side — "All" export. */
const drawHeadlineCompare = (doc: jsPDF, data: AffordabilityPdfData, y: number): number => {
  const n = data.reports.length;
  const gap = 4;
  const w = (CW - gap * (n - 1)) / n;
  const funds = data.summary.totalFundsAvailable > 0;
  const rowsPer = funds ? 5 : 4;
  const h = 40 + rowsPer * 7.2;

  data.reports.forEach((r, i) => {
    const meta = TYPE_META[r.type];
    const x = M + i * (w + gap);
    fillRound(doc, x, y, w, h, SOFT, 3);
    // Coloured header strip
    doc.setFillColor(...meta.accent);
    doc.roundedRect(x, y, w, 11, 3, 3, 'F');
    doc.rect(x, y + 6, w, 5, 'F');
    write(doc, meta.label, x + 4, y + 7.4, { size: 11, bold: true, color: WHITE });
    write(doc, meta.rule, x + w - 4, y + 7.2, { size: 6.5, bold: true, color: WHITE, align: 'right' });

    write(doc, r.maxPropertyPriceIsTarget ? 'TARGET PROPERTY PRICE' : 'MAX PROPERTY PRICE', x + 4, y + 18.5, { size: 6.3, bold: true, color: MUTED });
    write(doc, money(r.maxPropertyPrice), x + 4, y + 28, { size: 17, bold: true, color: r.eligibility.eligible ? NAVY : MUTED });
    if (!r.eligibility.eligible) pill(doc, 'NOT ELIGIBLE', x + w - 4, y + 14.5, RED, WHITE, 6, 'right');

    const rows: [string, string][] = [
      ['Max loan', money(r.maxLoan)],
      ['Loan tenure', `${r.loanTenure} yrs`],
      ['Monthly instalment', money(maxLoanInstalment(r))],
      ['Instalment cap', money(r.instalmentCap)],
      ...(funds ? [['Affordable today', money(r.affordableToday)] as [string, string]] : []),
    ];
    rows.forEach(([label, value], j) => {
      const ry = y + 38 + j * 7.2;
      doc.setDrawColor(...LINE);
      doc.setLineWidth(0.2);
      doc.line(x + 4, ry - 4.4, x + w - 4, ry - 4.4);
      write(doc, label, x + 4, ry, { size: 7.5, color: MUTED });
      const highlight = label === 'Affordable today';
      write(doc, value, x + w - 4, ry, { size: 8.5, bold: true, color: highlight ? meta.accent : NAVY, align: 'right' });
    });
  });
  const noteH = write(
    doc,
    `Monthly instalment is on the max loan at the ${pct(data.inputs.bankRate)} bank rate. Max loan is tested at the ${pct(data.inputs.stressTestRate)} stress-test rate.`
      + (hdbLoanReport(data) ? ` HDB is on an HDB loan: ${pct(hdbLoanReport(data)!.basis.interestRate)} rate, tested at the ${pct(hdbLoanReport(data)!.basis.stressRate)} HDB floor.` : '')
      + (data.reports.some((r) => !r.eligibility.eligible) ? ' A type marked Not eligible is shown for reference only.' : ''),
    M,
    y + h + 4.5,
    { size: 6.8, italic: true, color: MUTED, maxWidth: CW },
  );
  return y + h + 4.5 + noteH + 3;
};

const drawProfileTable = (doc: jsPDF, data: AffordabilityPdfData, y: number): number => {
  const { inputs, summary } = data;
  const joint = inputs.includeAdditionalBorrower;
  const borrowers = [
    { b: inputs.primary, name: data.primaryBorrowerName },
    ...(joint ? [{ b: inputs.additional, name: data.additionalBorrowerName }] : []),
  ];
  const head = [
    '',
    ...borrowers.map(({ name }, i) => (name?.trim() ? name.trim() : joint ? `Borrower ${i + 1}` : 'Borrower')),
    ...(joint ? ['Combined'] : []),
  ];
  const combined = (v: string) => (joint ? [v] : []);
  const body: string[][] = [
    ['Age', ...borrowers.map(({ b }) => `${b.age}`), ...combined(`${summary.iwaa} (income-weighted)`)],
    ['Employment', ...borrowers.map(({ b }) => (b.isSelfEmployed ? 'Self-employed' : 'Employed')), ...combined('')],
    ['Monthly income', ...borrowers.map(({ b }) => money(b.monthlyIncome)), ...combined(money(borrowers.reduce((a, { b }) => a + b.monthlyIncome, 0)))],
    ['Other annual income', ...borrowers.map(({ b }) => money(b.otherAnnualIncome)), ...combined(money(borrowers.reduce((a, { b }) => a + b.otherAnnualIncome, 0)))],
    ['Income counted by banks', ...borrowers.map(({ b }) => `${money(recognisedIncome(b))} / mth`), ...combined(`${money(summary.totalRecognisedIncome)} / mth`)],
    ['Existing monthly debt', ...borrowers.map(({ b }) => money(b.existingMonthlyDebt)), ...combined(money(summary.totalExistingDebt))],
  ];
  const labelW = joint ? 52 : 70;
  autoTable(doc, {
    startY: y,
    head: [head],
    body,
    ...cleanTableStyles,
    columnStyles: {
      0: { textColor: MUTED, cellWidth: labelW },
      ...Object.fromEntries(head.slice(1).map((_, i) => [i + 1, { halign: 'right' as const, fontStyle: 'bold' as const, textColor: NAVY }])),
    },
    didParseCell: (h) => {
      if (h.section === 'head' && h.column.index > 0) h.cell.styles.halign = 'right';
    },
    margin: { left: M, right: M, bottom: 18 },
    tableWidth: CW,
  });
  return (doc as any).lastAutoTable.finalY;
};

const drawAssumptionChips = (doc: jsPDF, data: AffordabilityPdfData, y: number): number => {
  const { inputs, summary } = data;
  const chips = [
    `Applied LTV ${inputs.ltv}%`,
    `${inputs.stressTestRateLabel} ${pct(inputs.stressTestRate)}`,
    `${inputs.bankRateLabel} ${pct(inputs.bankRate)}`,
    `Tenure requested ${inputs.tenure} yrs`,
    ...(hdbLoanReport(data) ? [`HDB loan: LTV ${hdbLoanReport(data)!.basis.ltv}%, HDB floor ${pct(hdbLoanReport(data)!.basis.stressRate)}`] : []),
    ...(summary.totalFundsAvailable > 0
      ? [`Cash ${money(summary.cashAvailable)} + CPF ${money(summary.cpfAvailable)}`]
      : []),
  ];
  let x = M;
  let rowY = y;
  chips.forEach((c) => {
    setFont(doc, { size: 7, bold: true });
    const w = doc.getTextWidth(c) + 5;
    if (x + w > M + CW) { x = M; rowY += 7; }
    pill(doc, c, x, rowY, SOFT, NAVY, 7);
    x += w + 2.5;
  });
  return rowY + 9;
};

// ── Page 1: Cover — same format as the other calculator reports ─────────

const coverFooter = (doc: jsPDF, logo: Logo | undefined, agent?: AgentInfo) => {
  const footerY = PAGE_H - 15;
  doc.setFillColor(...COVER_FOOTER_BG);
  doc.rect(0, footerY, PAGE_W, 15, 'F');
  if (logo) {
    const logoH = 7;
    doc.addImage(logo.dataUrl, 'PNG', 10, footerY + (15 - logoH) / 2, logoH * logo.aspect, logoH);
  } else {
    write(doc, 'Huttons', 10, footerY + 8, { size: 8, color: WHITE });
  }
  write(doc, '1', PAGE_W / 2, footerY + 8, { size: 8, color: WHITE, align: 'center' });
  if (agent?.Name) {
    write(doc, agent.Name, PAGE_W - 10, footerY + 5, { size: 8, bold: true, color: COVER_ORANGE, align: 'right' });
    if (agent.CEANumber) write(doc, `CEA: ${agent.CEANumber}`, PAGE_W - 10, footerY + 11, { size: 8, color: WHITE, align: 'right' });
  } else {
    write(doc, 'Max Affordability Report', PAGE_W - 10, footerY + 8, { size: 8, color: [200, 200, 200], align: 'right' });
  }
};

const renderCoverPage = async (doc: jsPDF, data: AffordabilityPdfData, logo?: Logo) => {
  const agent = data.agentInfo;
  doc.setFillColor(...COVER_BG);
  doc.rect(0, 0, PAGE_W, PAGE_H, 'F');
  doc.setFillColor(...COVER_ORANGE);
  doc.rect(0, 0, PAGE_W, 8, 'F');

  let y = 28;
  if (logo) {
    const logoH = 12;
    const logoW = logoH * logo.aspect;
    doc.addImage(logo.dataUrl, 'PNG', (PAGE_W - logoW) / 2, y - logoH + 2, logoW, logoH);
  } else {
    write(doc, 'HUTTONS', PAGE_W / 2, y, { size: 22, bold: true, color: WHITE, align: 'center' });
  }
  y += 10;
  write(doc, "SINGAPORE'S LARGEST PRIVATE REAL ESTATE AGENCY", PAGE_W / 2, y, { size: 9, color: [200, 200, 200], align: 'center' });
  y += 10;
  doc.setFillColor(...COVER_ORANGE);
  doc.rect(PAGE_W / 2 - 45, y, 90, 1.2, 'F');

  y += 22;
  write(doc, 'MAX AFFORDABILITY REPORT', PAGE_W / 2, y, { size: 22, bold: true, color: WHITE, align: 'center' });
  y += 10;
  const subtitle = data.reports.length === 1
    ? TYPE_META[data.reports[0].type].title
    : data.reports.map((r) => TYPE_META[r.type].label).join(' | ');
  write(doc, subtitle, PAGE_W / 2, y, { size: 12, bold: true, color: COVER_ORANGE, align: 'center' });
  y += 16;

  if (agent) {
    const boxW = 120;
    const boxH = 50;
    const boxX = (PAGE_W - boxW) / 2;
    doc.setFillColor(...COVER_CARD_BG);
    doc.setDrawColor(...COVER_ORANGE);
    doc.setLineWidth(2);
    doc.roundedRect(boxX, y, boxW, boxH, 3, 3, 'FD');

    const r = 18;
    const px = boxX + 10 + r;
    const py = y + boxH / 2;
    if (await drawAgentPhoto(doc, agent, px, py, r)) {
      doc.setDrawColor(...COVER_ORANGE);
      doc.setLineWidth(2);
      doc.circle(px, py, r, 'D');
    } else {
      doc.setFillColor(...COVER_ORANGE);
      doc.circle(px, py, r, 'F');
      const initials = agent.Name ? agent.Name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase() : 'U';
      write(doc, initials, px, py + 3, { size: 16, bold: true, color: WHITE, align: 'center' });
    }

    const tx = boxX + 10 + r * 2 + 15;
    let ty = y + 12;
    if (agent.Name) { write(doc, agent.Name, tx, ty, { size: 12, bold: true, color: WHITE }); ty += 7; }
    if (agent.CEANumber) {
      const label = 'CEA Registration: ';
      write(doc, label, tx, ty, { size: 9, color: WHITE });
      setFont(doc, { size: 9 });
      write(doc, agent.CEANumber, tx + doc.getTextWidth(label), ty, { size: 9, bold: true, color: COVER_ORANGE });
      ty += 6;
    }
    if (agent.Email) { write(doc, agent.Email, tx, ty, { size: 9, color: WHITE }); ty += 6; }
    if (agent.Mobile) write(doc, agent.Mobile, tx, ty, { size: 9, color: WHITE });
    y += boxH + 14;
  } else {
    y += 8;
  }

  if (data.profileName?.trim()) {
    y += 10;
    write(doc, 'Specially Prepared for', PAGE_W / 2, y, { size: 10, color: [200, 200, 200], align: 'center' });
    y += 9;
    write(doc, data.profileName.trim(), PAGE_W / 2, y, { size: 16, bold: true, color: COVER_ORANGE, align: 'center' });
    y += 12;
  }

  const genDate = new Date().toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' });
  write(doc, `Generated on: ${genDate}`, PAGE_W / 2, y, { size: 9, color: [200, 200, 200], align: 'center' });
  y += 10;
  doc.setFillColor(...COVER_ORANGE);
  doc.rect(PAGE_W / 2 - 40, y, 80, 0.8, 'F');
  y += 10;
  write(
    doc,
    'This report is for informational purposes only. Actual loan terms may vary. Subject to MAS regulations, HDB eligibility rules and lender policies.',
    PAGE_W / 2,
    y,
    { size: 7.5, italic: true, color: [160, 160, 160], align: 'center', maxWidth: PAGE_W - 50 },
  );

  coverFooter(doc, logo, agent);
};

// ── Page 2: Summary ──────────────────────────────────────────────────────

const renderSummaryPage = (doc: jsPDF, data: AffordabilityPdfData, logo?: Logo) => {
  doc.addPage();
  pageHeader(doc, 'Summary', ORANGE, logo);

  let y = 28;
  write(doc, 'Your Home Affordability', M, y, { size: 19, bold: true, color: NAVY });
  const borrowerNames = [data.primaryBorrowerName, data.inputs.includeAdditionalBorrower ? data.additionalBorrowerName : undefined]
    .map((n) => n?.trim()).filter(Boolean).join(' & ');
  const preparedFor = data.profileName?.trim() || borrowerNames;
  const types = data.reports.map((r) => TYPE_META[r.type].label).join(' | ');
  y += 6;
  write(doc, preparedFor ? `Prepared for ${preparedFor}  |  ${types}` : types, M, y, { size: 8.5, color: MUTED });
  y += 10;

  y = sectionTitle(doc, y, 'At a glance', ORANGE) + 3;
  y = data.reports.length === 1
    ? drawHeadlineSingle(doc, data, data.reports[0], y)
    : drawHeadlineCompare(doc, data, y);

  y = sectionTitle(doc, y, 'Your profile', ORANGE) + 1;
  y = drawProfileTable(doc, data, y) + 7;

  y = sectionTitle(doc, y, 'Key assumptions', ORANGE) + 2;
  drawAssumptionChips(doc, data, y);
};

// ── Property-type page ───────────────────────────────────────────────────

/**
 * Draws one property-type page. `compact` tightens tiles, gaps and table rows so the
 * page fits on a single sheet. Returns false when the content spilled past one page.
 */
const renderTypePage = (doc: jsPDF, data: AffordabilityPdfData, r: AffordabilityPdfTypeReport, logo: Logo | undefined, compact = false): boolean => {
  const { summary } = data;
  const meta = TYPE_META[r.type];
  const isMsr = r.type !== 'private';
  /** Vertical gap between blocks. */
  const gap = compact ? 1.5 : 3;
  doc.addPage();
  const firstPage = doc.getNumberOfPages();
  pageHeader(doc, `${meta.title} details`, meta.accent, logo);
  /** Continue on a fresh page when the next block wouldn't fit above the footer. */
  const ensureSpace = (y: number, needed: number): number => {
    if (y + needed <= CONTENT_BOTTOM) return y;
    doc.addPage();
    pageHeader(doc, `${meta.title} details (cont.)`, meta.accent, logo);
    return 26;
  };
  /**
   * Numbered section heading placed below the block ending at `y`. sectionTitle() draws
   * on the text baseline and its disc rises ~4.3mm above it, so offset by that rise to
   * keep the heading clear of the block above. Returns where the section's content starts.
   */
  const DISC_RISE = 4.3;
  const DISC_DROP = 1.7;
  const section = (y: number, title: string, step: number): number => {
    const baseline = y + (compact ? 3 : 4.5) + DISC_RISE;
    sectionTitle(doc, baseline, title, meta.accent, step);
    return baseline + DISC_DROP + (compact ? 2 : 3);
  };

  // Title block
  let y = 28;
  write(doc, meta.title, M, y, { size: 19, bold: true, color: meta.accent });
  pill(doc, meta.rule, PAGE_W - M, y - 5.5, meta.accent, WHITE, 7.5, 'right');
  y += 6;
  y += write(doc, typeDescription(r), M, y, { size: 8.5, color: MUTED, maxWidth: CW });
  y += 1;
  if (!r.eligibility.eligible) {
    y = noteBox(doc, y, 'Not eligible:', `${r.eligibility.reasons.join(' ')} The figures below are for reference only.`, RED_SOFT, RED) + 1;
  }
  if (r.eligibility.notes.length > 0) {
    y = noteBox(doc, y, 'Please check:', r.eligibility.notes.join(' '), AMBER_SOFT, AMBER) + 1;
  }

  // ── 1. How much you can borrow
  y = section(y, 'How much you can borrow', 1);
  const flowGap = 6;
  const tileW = (CW - flowGap * 3) / 4;
  const tileH = compact ? 23 : 26;
  const incomeSub = 'after income haircuts';
  const capSub = isMsr
    ? `lower of 30% MSR (${money(r.msr?.grossMSR ?? 0)}) and TDSR after debts (${money(summary.availableTDSR)})`
    : `55% of income (${money(summary.grossTDSR)}) less debts of ${money(summary.totalExistingDebt)}`;
  const tiles: [string, string, string][] = [
    ['Monthly income', money(summary.totalRecognisedIncome), incomeSub],
    ['Max monthly instalment', money(r.instalmentCap), capSub],
    ['Max loan', money(r.maxLoan), `over ${r.loanTenure} yrs, tested at ${pct(r.basis.stressRate)}`],
    [r.maxPropertyPriceIsTarget ? 'Target price' : 'Max property price', money(r.maxPropertyPrice), `max loan / ${r.basis.ltv}% LTV`],
  ];
  tiles.forEach(([label, value, sub], i) => {
    const x = M + i * (tileW + flowGap);
    const last = i === tiles.length - 1;
    statTile(doc, x, y, tileW, tileH, label, value, sub, last
      ? { fill: meta.accent, labelColor: WHITE, valueColor: WHITE, subColor: WHITE, valueSize: 12.5 }
      : { valueSize: 12.5 });
    if (!last) chevron(doc, x + tileW + flowGap / 2, y + tileH / 2, meta.accent);
  });
  y += tileH;

  if (summary.totalFundsAvailable > 0) {
    y = noteBox(
      doc,
      y + gap,
      'Affordable today:',
      `with cash of ${money(summary.cashAvailable)} and CPF of ${money(summary.cpfAvailable)}, the highest price you can complete on now is ${money(r.affordableToday)} (after down payment, BSD and ABSD).`,
      meta.soft,
      meta.accent,
    );
  }
  if (isMsr && r.msr?.aboveCeiling) {
    y = noteBox(
      doc,
      y + gap,
      'Income ceiling:',
      `household income is above the ${money(r.msr.incomeCeiling)} ${r.msr.ceilingLabel}: ${r.msr.ceilingConsequence}. The max loan above still counts the full income.`,
      AMBER_SOFT,
      AMBER,
    );
  }

  // ── 2. The property you're considering
  y = section(y, `The property you're considering: ${money(summary.propertyPrice)}`, 2);
  const cardGap = 4;
  const cardW = (CW - cardGap * 2) / 3;
  const cardH = compact ? 20 : 23;
  statTile(doc, M, y, cardW, cardH, `Loan needed (${r.basis.ltv}% LTV)`, money(r.requiredLoan), `vs your max loan of ${money(r.maxLoan)}`);
  statTile(doc, M + cardW + cardGap, y, cardW, cardH, 'Monthly mortgage', money(r.monthlyMortgageRequired), `at ${pct(r.basis.interestRate)} over ${r.loanTenure} yrs`);
  statTile(doc, M + (cardW + cardGap) * 2, y, cardW, cardH, 'Minimum cash', money(r.minimumCashNeeded),
    r.basis.hdbLoan
      ? 'the OTP deposit only — CPF can pay the rest'
      : r.type === 'hdb' ? 'incl. the OTP deposit, paid in cash' : `${Math.round(r.basis.cashPercent * 100)}% of the price, cash only`);
  y += cardH + gap;

  if (r.loanShortfall > 0) {
    y = noteBox(
      doc,
      y,
      `Loan shortfall of ${money(r.loanShortfall)}.`,
      `The loan needed (${money(r.requiredLoan)}) is above your ${meta.label} max loan (${money(r.maxLoan)}). Bridge it with more cash, or with the income support below.`,
      RED_SOFT,
      RED,
    );
  } else {
    y = noteBox(
      doc,
      y,
      'Within your limit.',
      `The loan needed is ${money(r.maxLoan - r.requiredLoan)} below your ${meta.label} max loan, so no extra income support is needed.`,
      GREEN_SOFT,
      GREEN,
    );
  }

  // ── 3. Bridging the shortfall (only when income support is needed)
  let step = 3;
  if (r.pledgedFundsNeeded > 0) {
    y = ensureSpace(y, compact ? 52 : 58);
    y = section(y, 'Ways to bridge the shortfall', step);
    step += 1;
    const lead = isMsr
      ? `You need about ${money(r.extraMonthlyIncomeNeeded)} more income a month: MSR needs ${money(r.msrExtraIncome)} and TDSR needs ${money(r.tdsrExtraIncome)}, and the larger applies. Banks will count either of these instead:`
      : `You need about ${money(r.extraMonthlyIncomeNeeded)} more income a month to pass TDSR. Banks will count either of these instead:`;
    // Baseline sits one cap height below `y`; write() returns whole line heights, so
    // trade the last line's leading for its ~1mm descender.
    const leadCap = 2.4;
    const leadH = write(doc, lead, M, y + leadCap, { size: 8.5, color: INK, maxWidth: CW });
    y += leadCap + leadH - lineMm(8.5) + 1 + gap;
    const orW = 10;
    const optW = (CW - orW) / 2;
    const optH = compact ? 20 : 22;
    statTile(doc, M, y, optW, optH, 'Pledge a fixed deposit', money(r.pledgedFundsNeeded), 'locked for 48 months, counted at 100%', { fill: meta.soft, valueColor: meta.accent, valueSize: 14 });
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.3);
    doc.circle(M + optW + orW / 2, y + optH / 2, 4, 'FD');
    write(doc, 'OR', M + optW + orW / 2, y + optH / 2 + 1.1, { size: 7, bold: true, color: MUTED, align: 'center' });
    statTile(doc, M + optW + orW, y, optW, optH, 'Or show unpledged assets', money(r.unpledgedFundsNeeded), 'cash, shares or bonds, counted at 30%', { fill: meta.soft, valueColor: meta.accent, valueSize: 14 });
    y += optH;
    if (r.plannedMix && r.plannedMix.unpledged > 0) {
      y = noteBox(
        doc,
        y + gap,
        'Your planned mix:',
        `${money(r.plannedMix.unpledged)} unpledged plus ${money(r.plannedMix.pledged)} pledged, ${money(r.plannedMix.unpledged + r.plannedMix.pledged)} in total.`,
        SOFT,
        NAVY,
      );
    }
  }

  // ── Upfront costs: down payment (left) beside stamp duty and the total (right)
  const sd = data.stampDuty;
  const plan = getDownPaymentPlan(r.type, summary.propertyPrice, r.requiredLoan, r.basis.ltv);
  const fees = getClosingFees(r.type, summary.propertyPrice, r.basis.hdbLoan);
  // Row height = 8pt text (autoTable line height 1.15) + vertical padding.
  const cellPadY = compact ? 1.3 : 2;
  const rowH = lineMm(8, 1.15) + cellPadY * 2;
  // The stamp duty table grows a row per fee; reserve room so it doesn't split.
  const tableRows = 1 + Math.max(plan.stages.length + 1, fees.length + 3);
  y = ensureSpace(y, 12 + tableRows * rowH + 16);
  y = section(y, 'Upfront costs', step);
  const initialOutlay = plan.upfront + sd.total + fees.reduce((sum, fee) => sum + fee.amount, 0);
  const halfGap = 5;
  const halfW = (CW - halfGap) / 2;
  const bold = (content: string, extra: Record<string, unknown> = {}) => ({ content, styles: { fontStyle: 'bold', textColor: NAVY, ...extra } });
  const accentCell = (content: string) => ({ content, styles: { fontStyle: 'bold', textColor: WHITE, fillColor: meta.accent } });
  const tableBase = {
    ...cleanTableStyles,
    styles: { ...cleanTableStyles.styles, cellPadding: { top: cellPadY, bottom: cellPadY, left: 3, right: 3 } },
    columnStyles: { 0: { textColor: INK }, 1: { halign: 'right' as const, fontStyle: 'bold' as const, textColor: NAVY, cellWidth: 28 } },
    pageBreak: 'avoid' as const,
    tableWidth: halfW,
  };
  const startY = y;
  const startPage = doc.getCurrentPageInfo().pageNumber;
  autoTable(doc, {
    ...tableBase,
    startY,
    head: [[`Down payment (${plan.upfrontPct}%)`, '']],
    body: [
      ...plan.stages.map((st) => [st.label, money(st.amount)]),
      [bold('Down payment'), bold(money(plan.upfront))],
    ] as any,
    margin: { left: M, right: M + halfW + halfGap, bottom: 18 },
  });
  const leftBottom = (doc as any).lastAutoTable.finalY;
  doc.setPage(startPage);
  autoTable(doc, {
    ...tableBase,
    startY,
    head: [['Stamp duty & fees', '']],
    body: [
      ["Buyer's Stamp Duty", money(sd.bsd)],
      [`Additional Buyer's Stamp Duty (${sd.absdRatePercent}%)`, money(sd.absd)],
      ...fees.map((fee) => [fee.label, money(fee.amount)]),
      [accentCell('Total initial outlay'), accentCell(money(initialOutlay))],
    ] as any,
    margin: { left: M + halfW + halfGap, right: M, bottom: 18 },
  });
  y = Math.max(leftBottom, (doc as any).lastAutoTable.finalY) + (compact ? 3 : 4);

  const cashOnly = Math.min(plan.upfront, r.minimumCashNeeded);
  const notes = [
    `At least ${money(cashOnly)} of the down payment must be cash; the rest (${money(Math.max(0, plan.upfront - cashOnly))}) can come from cash or CPF.`,
    r.type === 'hdb'
      ? 'HDB resale: the Option and Exercise Fees are paid on the OTP and the balance at completion.'
      : `Paid progressively for a new launch${r.type === 'ec' ? ' (EC Normal Payment Scheme)' : ''}.${plan.deferred > 0 ? ` A further ${money(plan.deferred)} (${plan.deferredPct}%) is due later in the schedule.` : ''}`,
  ];
  if (sd.absdRemissionApplied) notes.splice(1, 0, 'ABSD shown after the married-couple remission.');
  notes.forEach((n) => {
    y += write(doc, `-  ${n}`, M, y, { size: 7.2, color: MUTED, maxWidth: CW });
  });
  // `y` is the baseline after the last note; its descenders sit just below.
  return doc.getNumberOfPages() === firstPage && y - lineMm(7.2) <= CONTENT_BOTTOM;
};

/** One sheet per property type: redraw compactly when the normal layout spills over. */
const renderTypePageFitted = (doc: jsPDF, data: AffordabilityPdfData, r: AffordabilityPdfTypeReport, logo?: Logo) => {
  const before = doc.getNumberOfPages();
  if (renderTypePage(doc, data, r, logo)) return;
  while (doc.getNumberOfPages() > before) doc.deletePage(doc.getNumberOfPages());
  renderTypePage(doc, data, r, logo, true);
};

// ── Notes page ───────────────────────────────────────────────────────────

const renderNotesPage = (doc: jsPDF, data: AffordabilityPdfData, logo?: Logo) => {
  const { inputs, summary } = data;
  doc.addPage();
  pageHeader(doc, 'How the figures are worked out', NAVY, logo);
  let y = 28;
  write(doc, 'How the figures are worked out', M, y, { size: 17, bold: true, color: NAVY });
  y += 6;
  y += write(doc, 'Every figure in this report follows Singapore bank lending rules (MAS) and the inputs on the profile page.', M, y, { size: 8.5, color: MUTED, maxWidth: CW });
  y += 5;

  y = sectionTitle(doc, y, 'Step by step', ORANGE) + 1;
  const reported = new Set(data.reports.map((r) => r.type));
  const steps: [string, string][] = [
    [
      'Income counted',
      `Salary at 100% (70% if self-employed) plus 70% of commission, bonus and other variable income: ${money(summary.totalRecognisedIncome)} a month.${inputs.includeAdditionalBorrower ? ' Joint borrowers are added together.' : ''}`,
    ],
    [
      'Instalment limit',
      `TDSR: all debt repayments within 55% of income (${money(summary.grossTDSR)} less existing debts of ${money(summary.totalExistingDebt)} = ${money(summary.availableTDSR)}).${reported.has('ec') || reported.has('hdb') ? ' EC and HDB also apply MSR: the home loan alone within 30% of income, counted in full. The lower limit applies.' : ''}`,
    ],
    [
      'Max loan',
      `The loan that instalment limit can repay over the loan tenure, tested at the ${pct(inputs.stressTestRate)} stress-test rate rather than today's bank rate.`
        + (hdbLoanReport(data) ? ` An HDB loan is tested at HDB's ${pct(hdbLoanReport(data)!.basis.stressRate)} floor instead.` : ''),
    ],
    ['Max property price', `Max loan divided by the ${inputs.ltv}% loan-to-value limit.`],
    [
      'Loan tenure',
      `The shortest of the tenure requested (${inputs.tenure} yrs), the years to age ${inputs.ltv === 55 ? 75 : 65} (${summary.ageBasedCap} yrs, using age ${summary.effectiveAgeUsed}${inputs.includeAdditionalBorrower ? ', income-weighted' : ''}) and the property type's maximum.`,
    ],
    [
      'Income support',
      'If the price needs a bigger loan, the missing income can be shown with a fixed deposit pledged for 48 months (counted at 100%) or unpledged assets (counted at 30%).',
    ],
  ];
  autoTable(doc, {
    startY: y,
    body: steps,
    ...cleanTableStyles,
    styles: { ...cleanTableStyles.styles, fontSize: 8, valign: 'top', cellPadding: { top: 2.4, bottom: 2.4, left: 3, right: 3 } },
    columnStyles: { 0: { fontStyle: 'bold', textColor: NAVY, cellWidth: 38 }, 1: { textColor: INK } },
    margin: { left: M, right: M, bottom: 18 },
    tableWidth: CW,
  });
  y = (doc as any).lastAutoTable.finalY + 8;

  y = sectionTitle(doc, y, 'Rules applied', ORANGE) + 1;
    const sd = data.stampDuty;
  const rules: string[][] = [
    ['TDSR', '55% of income, for every property type.'],
    ['MSR', '30% of income, for EC and HDB loans only. Counts the home loan, not other debts.'],
    ['Eligibility', 'EC: household income up to $16,000; a couple (one Singapore Citizen, the other Citizen or PR, both 21+) or Joint Singles (all Citizens, 35+). HDB: Singapore Citizen singles 35+, couples, or Joint Singles; no other property owned. HDB loan: at least one Citizen, income up to $16,000 for families or $8,000 for singles. Ceilings are eligibility tests; MSR counts the full income.'],
    ['Maximum tenure', `Private and EC: ${inputs.ltv === 55 ? 35 : 30} years. HDB on a bank loan: ${inputs.ltv === 55 ? 30 : 25} years. Also capped at age ${inputs.ltv === 55 ? 75 : 65}.`],
    ['Loan-to-value', `${inputs.ltv}% applied. Minimum cash: ${inputs.ltv === 55 ? '10%' : '5%'} of the price.`],
    ['Interest rates', `Max loan tested at ${pct(inputs.stressTestRate)} (${inputs.stressTestRateLabel}); instalments shown at ${pct(inputs.bankRate)} (${inputs.bankRateLabel}).`],
    ...(hdbLoanReport(data)
      ? [['HDB loan', `${pct(hdbLoanReport(data)!.basis.interestRate)} rate, tested at HDB's ${pct(hdbLoanReport(data)!.basis.stressRate)} floor; ${hdbLoanReport(data)!.basis.ltv}% LTV, up to 25 years and age 65; no cash-only minimum.`]]
      : []),
    [
      'Stamp duty',
      `On ${money(sd.propertyPrice)}: BSD ${money(sd.bsd)} (IRAS progressive rates) and ABSD ${sd.absdRatePercent}% = ${money(sd.absd)}${sd.absdRemissionApplied && sd.absdAmountBeforeRemission != null ? ` (was ${money(sd.absdAmountBeforeRemission)} before married-couple remission)` : ''}.`,
    ],
  ];
  autoTable(doc, {
    startY: y,
    body: rules,
    ...cleanTableStyles,
    styles: { ...cleanTableStyles.styles, fontSize: 8, valign: 'top' },
    columnStyles: { 0: { fontStyle: 'bold', textColor: NAVY, cellWidth: 38 }, 1: { textColor: INK } },
    margin: { left: M, right: M, bottom: 18 },
    tableWidth: CW,
  });
  y = (doc as any).lastAutoTable.finalY + 8;

  noteBox(
    doc,
    Math.min(y, CONTENT_BOTTOM - 20),
    'Please note:',
    'this report is an estimate for planning only. The final loan amount, tenure and rates depend on the bank\'s own assessment, HDB eligibility and prevailing MAS rules. Sources: MAS Notice 632, HDB and IRAS.',
    SOFT,
    MUTED,
  );
};

// ── Export filename: "<profile name> - Max Affordability Calculator (<types>) - <date time>.pdf" ──

const sanitizeFilePart = (name?: string): string =>
  (name ?? '').trim().replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();

const buildExportFilename = (data: AffordabilityPdfData): string => {
  const borrowers = [
    sanitizeFilePart(data.primaryBorrowerName),
    ...(data.inputs.includeAdditionalBorrower ? [sanitizeFilePart(data.additionalBorrowerName)] : []),
  ].filter(Boolean).join(' & ');
  const who = sanitizeFilePart(data.profileName) || borrowers;
  const types = data.reports.length === 3 ? 'All' : data.reports.map((r) => TYPE_META[r.type].label).join(' & ');
  // ":" is not filename-safe (Windows) — use dots in the time instead.
  const ts = new Date().toLocaleString('en-SG', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).replace(/:/g, '.');
  return `${[who, `Max Affordability Calculator (${types})`, ts].filter(Boolean).join(' - ')}.pdf`;
};

/** Builds the report document (no download) — split out so it can be rendered headlessly. */
export const buildAffordabilityPdf = async (data: AffordabilityPdfData): Promise<jsPDF> => {
  // compress: the Huttons logo is a 4210x1035 PNG that jsPDF embeds as raw RGBA —
  // 17 MB of a 17 MB report. Flate-compressing the streams brings it to ~50 KB.
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });

  let logo: Logo | undefined;
  try {
    const logoImg = await loadImageEl(HuttonsWhiteLogo);
    logo = { dataUrl: HuttonsWhiteLogo, aspect: logoImg.width / logoImg.height };
  } catch { /* text fallback */ }

  await renderCoverPage(doc, data, logo);
  renderSummaryPage(doc, data, logo);
  data.reports.forEach((r) => renderTypePageFitted(doc, data, r, logo));
  renderNotesPage(doc, data, logo);

  const total = doc.getNumberOfPages();
  // The cover carries its own dark footer.
  for (let p = 2; p <= total; p++) {
    doc.setPage(p);
    pageFooter(doc, p, total, data.agentInfo);
  }
  return doc;
};

// ── Main Entry Point ─────────────────────────────────────────────────────
export const exportAffordabilityToPDF = async (
  data: AffordabilityPdfData,
  filename?: string,
): Promise<void> => {
  const doc = await buildAffordabilityPdf(data);
  await savePdfDocument(doc, filename ?? buildExportFilename(data));
};
